import { Field, SelectField } from '@today/ui-components'
import { Badge, Button } from '@today/ui-primitives'
import { type ReactElement, useState } from 'react'
import type { ContactPointView, Translate } from './customer-types'

const TYPES = ['email', 'mobile', 'landline'] as const

/**
 * Props for {@link ContactPointListView}. The move/primary/delete/add handlers
 * are present only when the viewer may edit contact points.
 */
export interface ContactPointListViewProps {
	t: Translate
	points: ContactPointView[]
	onMove?: (id: string, direction: 'up' | 'down') => void
	onSetPrimary?: (id: string) => void
	onDelete?: (id: string) => void
	onAdd?: (input: { type: string; value: string }) => void
}

interface RowProps {
	t: Translate
	point: ContactPointView
	isFirst: boolean
	isLast: boolean
	props: ContactPointListViewProps
}

function MoveButtons({ t, point, isFirst, isLast, props }: RowProps) {
	if (!props.onMove) return null
	return (
		<>
			<Button
				size="sm"
				variant="muted"
				isDisabled={isFirst}
				aria-label={`${t('actions.moveUp')} ${point.value}`}
				onPress={() => props.onMove?.(point.id, 'up')}
			>
				{t('actions.moveUp')}
			</Button>
			<Button
				size="sm"
				variant="muted"
				isDisabled={isLast}
				aria-label={`${t('actions.moveDown')} ${point.value}`}
				onPress={() => props.onMove?.(point.id, 'down')}
			>
				{t('actions.moveDown')}
			</Button>
		</>
	)
}

function RowActions(row: RowProps) {
	const { t, point, props } = row
	return (
		<>
			<MoveButtons {...row} />
			{props.onSetPrimary && !point.isPrimary ? (
				<Button
					size="sm"
					variant="secondary"
					aria-label={`${t('actions.setPrimary')} ${point.value}`}
					onPress={() => props.onSetPrimary?.(point.id)}
				>
					{t('actions.setPrimary')}
				</Button>
			) : null}
			{props.onDelete ? (
				<Button
					size="sm"
					variant="danger"
					aria-label={`${t('actions.delete')} ${point.value}`}
					onPress={() => props.onDelete?.(point.id)}
				>
					{t('actions.delete')}
				</Button>
			) : null}
		</>
	)
}

function PointRow(row: RowProps) {
	const { t, point } = row
	return (
		<li className="flex items-center gap-2 border-b border-ds-border py-2 text-ds-sm">
			<span className="flex-1 text-ds-text">{point.value}</span>
			{point.isPrimary ? <Badge size="sm">{t('contact.primary')}</Badge> : null}
			{point.verified ? (
				<Badge size="sm" variant="success">
					{t('contact.verified')}
				</Badge>
			) : null}
			{point.invalid ? (
				<Badge size="sm" variant="danger">
					{t('contact.invalid')}
				</Badge>
			) : null}
			<RowActions {...row} />
		</li>
	)
}

function AddContactForm({ t, onAdd }: { t: Translate; onAdd: (input: { type: string; value: string }) => void }) {
	const [draft, setDraft] = useState({ type: 'email', value: '' })
	return (
		<div className="flex items-end gap-2">
			<SelectField
				label={t('contact.type')}
				value={draft.type}
				onValueChange={(type) => setDraft({ ...draft, type })}
				options={TYPES.map((type) => ({ id: type, text: t(`contact.${type}`) }))}
			/>
			<Field
				label={t('contact.value')}
				value={draft.value}
				onChange={(e) => setDraft({ ...draft, value: e.target.value })}
			/>
			<Button
				onPress={() => {
					onAdd(draft)
					setDraft({ ...draft, value: '' })
				}}
			>
				{t('actions.addContact')}
			</Button>
		</div>
	)
}

/** Contact points grouped by type in priority order, with reorder / primary / delete / add (organism). */
export function ContactPointListView(props: ContactPointListViewProps): ReactElement {
	const { t } = props
	return (
		<section className="flex flex-col gap-4">
			{TYPES.map((type) => {
				const group = props.points.filter((p) => p.type === type)
				if (group.length === 0) return null
				return (
					<div key={type}>
						<h3 className="text-ds-sm font-semibold text-ds-text-muted">{t(`contact.${type}`)}</h3>
						<ul>
							{group.map((point, index) => (
								<PointRow
									key={point.id}
									t={t}
									point={point}
									isFirst={index === 0}
									isLast={index === group.length - 1}
									props={props}
								/>
							))}
						</ul>
					</div>
				)
			})}
			{props.onAdd ? <AddContactForm t={t} onAdd={props.onAdd} /> : null}
		</section>
	)
}
